import axios from 'axios';
import { notifyAccessEnded } from '../utils/accessEnded';   // CEV-S3
import { apiRequest } from '../authConfig';
// UX-S2 — friendly error toast helper. Lives in its own file (no
// transitive imports) so a Jest test can pin its precedence without
// dragging in axios / MSAL.
import { friendlyErrorMessage } from './friendlyErrorMessage';
export { friendlyErrorMessage };

// Environment-based API configuration (allow override via env)
const getApiBaseUrl = () => {
  const fromEnv = process.env.REACT_APP_API_BASE_URL;
  if (fromEnv && fromEnv.trim().length > 0) {
    const trimmed = fromEnv.replace(/\/$/, '');
    const hasApiSuffix = /\/api$/i.test(trimmed);
    return hasApiSuffix ? trimmed : `${trimmed}/api`;
  }
  if (process.env.NODE_ENV === 'production') {
    // In production, use same-origin relative base. SPA and API share the App Service.
    return '/api';
  }
  // Default to HTTPS dev backend to avoid CORS redirect issues on preflight
  return 'https://localhost:7040/api';
};

const API_BASE_URL = getApiBaseUrl();
if (typeof window !== 'undefined') {
  window.__API_BASE_URL__ = API_BASE_URL;
  console.log('[API] Base URL:', API_BASE_URL);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Allow setting Bearer token globally for this axios instance
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('Authorization header set in api.js');
  } else {
    delete api.defaults.headers.common['Authorization'];
  }

};

// Track global in-flight requests for top progress bar
const inc = () => { window.__loadingCount = (window.__loadingCount || 0) + 1; window.dispatchEvent(new Event('hrapp.loading.change')); };
const dec = () => { window.__loadingCount = Math.max(0, (window.__loadingCount || 0) - 1); window.dispatchEvent(new Event('hrapp.loading.change')); };

// Proactive auth: attach Bearer access token for our API scope if not already present
api.interceptors.request.use(async (config) => {
  try {
    // `silent: true` requests (e.g. batched-save flushes) bypass the global
    // progress bar so inline grid edits do not flash the TopBar indicator.
    if (!config.silent) inc();
    if (!config.headers) config.headers = {};
    const hasAuth = !!config.headers['Authorization'] || !!config.headers['authorization'];
    // Only attempt if no Authorization yet and we have scopes configured
    if (!hasAuth && Array.isArray(apiRequest.scopes) && apiRequest.scopes.length > 0) {
      const msal = window.__msalInstance; // created in src/index.js
      if (msal) {
        const accounts = msal.getAllAccounts();
        if (accounts && accounts.length > 0) {
          const request = { ...apiRequest, account: accounts[0] };
          try {
            const result = await msal.acquireTokenSilent(request);
            if (result?.accessToken) {
              config.headers['Authorization'] = `Bearer ${result.accessToken}`;
            }
          } catch (silentErr) {
            console.warn('[API] acquireTokenSilent failed, will try popup on next user action', silentErr);
          }
        }
      }
    }
  } catch (e) {
    console.warn('[API] request interceptor error', e);
  }
  return config;
});

// Safety net: force HTTPS dev backend if someone overrides to http://5022


// Response interceptor to extract data and handle 401 with interactive fallback
api.interceptors.response.use(
  (response) => {
    if (!response.config?.silent) dec();
    // Return just the data part of the response
    return response.data;
  },
  async (error) => {
    if (!error.config?.silent) dec();
    notifyAccessEnded(error);   // CEV-S3
    try {
      const status = error?.response?.status;
      const original = error?.config || {};

      // 401 guard: try to obtain access token and retry once
      if (status === 401 && !original.__isRetry401) {
        const msal = typeof window !== 'undefined' ? window.__msalInstance : null;
        if (msal && Array.isArray(apiRequest.scopes) && apiRequest.scopes.length > 0) {
          const accounts = msal.getAllAccounts?.() || [];
          if (accounts.length > 0) {
            const request = { ...apiRequest, account: accounts[0] };
            // 1) Silent refresh
            try {
              const silent = await msal.acquireTokenSilent(request);
              if (silent?.accessToken) {
                setAuthToken(silent.accessToken);
                if (typeof window !== 'undefined') window.dispatchEvent(new Event('hrapp.auth.clear'));
                original.headers = original.headers || {};
                original.headers['Authorization'] = `Bearer ${silent.accessToken}`;
                original.__isRetry401 = true;
                return api(original);
              }
            } catch {}

            // 2) Interactive popup
            try {
              if (!window.__auth_popup_in_progress) {
                window.__auth_popup_in_progress = true;
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('hrapp.toast', { detail: { sev: 'info', msg: 'Refreshing session...' } }));
                }
                const popup = await msal.acquireTokenPopup(request);
                if (popup?.accessToken) {
                  setAuthToken(popup.accessToken);
                  original.headers = original.headers || {};
                  original.headers['Authorization'] = `Bearer ${popup.accessToken}`;
                  original.__isRetry401 = true;
                  return api(original);
                }
              }
            } catch (popupErr) {
              // Both silent and popup failed - session is truly expired
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('hrapp.toast', {
                  detail: {
                    sev: 'error',
                    msg: 'Session expired. Please refresh the page to sign in again.',
                    persistent: true
                  }
                }));
              }
              // Don't show additional error toast for 401
              return Promise.reject(error);
            } finally {
              window.__auth_popup_in_progress = false;
            }
          }
        }
      }

      // Default error toast (compact, user-friendly) - skip for 401 (already handled above)
      // UX-S2 — never JSON.stringify the body. Object-shaped error
      // responses get reduced to their human-readable message via the
      // exported friendlyErrorMessage helper.
      if (status !== 401) {
        const msg = friendlyErrorMessage(error);
        const sev = status && status >= 500 ? 'error' : 'warning';
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('hrapp.toast', { detail: { sev, msg } }));
        }
      }
    } catch {}
    return Promise.reject(error);
  }
);

// S3 — contracts and salaries, annexes, termination, new hire, salary register.
export const employmentService = {
  getSummary: (personId, asOf) => api.get(`/employment/person/${personId}`, { params: asOf ? { asOf } : {} }),
  getRegister: (params) => api.get('/employment/salary-register', { params }),
  exportRegister: (params) => api.get('/employment/salary-register/export', { params, responseType: 'blob' }),
  getLegalBases: () => api.get('/employment/legal-bases'),
  addAnnex: (contractId, annex) => api.post(`/employment/contracts/${contractId}/annexes`, annex),
  updateAnnex: (annexId, annex) => api.put(`/employment/annexes/${annexId}`, annex),
  signAnnex: (annexId, body) => api.post(`/employment/annexes/${annexId}/sign`, body),
  deleteAnnex: (annexId) => api.delete(`/employment/annexes/${annexId}`),
  terminationPreview: (employeeId, date) => api.get(`/employment/employees/${employeeId}/termination-preview`, { params: { date } }),
  terminate: (employeeId, body) => api.post(`/employment/employees/${employeeId}/termination`, body),
  cancelTermination: (employeeId) => api.delete(`/employment/employees/${employeeId}/termination`),
  newHire: (body) => api.post('/employment/new-hire', body),
};

export const employeeService = {
  getAll: () => api.get('/Employees'),
  getById: (id) => api.get(`/Employees/${id}`),
  getByPerson: (personId) => api.get(`/Employees/by-person/${personId}`),
  create: (employee) => api.post('/Employees', employee),
  update: (id, employee) => api.put(`/Employees/${id}`, employee),
  delete: (id) => api.delete(`/Employees/${id}`),
  linkContract: (employeeId, contractId) => api.post(`/Employees/${employeeId}/link-contract/${contractId}`),
  setCurrentContract: (employeeId, contractId) => api.post(`/Employees/${employeeId}/set-current-contract/${contractId}`),
  unlinkCurrentContract: (employeeId) => api.delete(`/Employees/${employeeId}/unlink-current-contract`),
  lookup: () => api.get('/employees/lookup'),
};

// RA-S5 (2026-07-17) — assignmentService retired. The whole personal-
// assignment layer (RA CRUD, load preview, availability, bulk assign)
// went with the ResourceAssignment UI in RA-S5; RA + DailyAllocation
// entities/tables live until RA-S6's schema drop. employeeService
// dropped getAvailability + getLoad here too — both were assignment-
// drawer-only helpers with no other caller.

export const positionService = {
  getAll: () => api.get('/Positions'),
  getById: (id) => api.get(`/Positions/${id}`),
  create: (position) => api.post('/Positions', position),
  update: (id, position) => api.put(`/Positions/${id}`, position),
  delete: (id) => api.delete(`/Positions/${id}`)
};

export const departmentService = {
  getAll: () => api.get('/Departments'),
  getById: (id) => api.get(`/Departments/${id}`),
  create: (department) => api.post('/Departments', department),
  update: (id, department) => api.put(`/Departments/${id}`, department),
  delete: (id) => api.delete(`/Departments/${id}`),
  searchEmployees: (query = '', limit = 20) => api.get(`/Departments/employees/search?query=${encodeURIComponent(query)}&limit=${limit}`)
};

export const seniorityLevelService = {
  getAll: () => api.get('/SeniorityLevels'),
  getById: (id) => api.get(`/SeniorityLevels/${id}`),
  create: async (level) => {
    console.log('seniorityLevelService.create called with data:', level);
    console.log('Making POST request to:', `${API_BASE_URL}/SeniorityLevels`);
    try {
      const response = await api.post('/SeniorityLevels', level);
      console.log('seniorityLevelService.create response:', response);
      return response;
    } catch (error) {
      console.error('seniorityLevelService.create error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  },
  update: (id, level) => api.put(`/SeniorityLevels/${id}`, level),
  delete: (id) => api.delete(`/SeniorityLevels/${id}`)
};

export const contractService = {
  getAll: () => api.get('/Contracts'),
  getById: (id) => api.get(`/Contracts/${id}`),
  getByPersonId: (personId) => api.get(`/Contracts/by-person/${personId}`),
  getAllByPersonId: (personId) => api.get(`/Contracts/all-by-person/${personId}`),
  getDuplicates: () => api.get('/Contracts/duplicates'),
  create: (contract) => api.post('/Contracts', contract),
  update: (id, contract) => api.put(`/Contracts/${id}`, contract),
  delete: (id, deleteFile = true) => api.delete(`/Contracts/${id}?deleteFile=${deleteFile}`),
  extractFromFile: (file) => {
    const formData = new FormData();
    formData.append('contractFile', file);
    return api.post('/contract-processing/extract-employee-data', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  createEmployeeFromContract: (contractId) => api.post(`/Contracts/${contractId}/create-employee`),
  // Upload document to Azure Blob Storage with new naming convention
  // Folder: {contractNumber}_{firstName}_{lastName}/
  // File: {contractNumber}_{firstName}_{lastName}_{documentType}_v{version}.{ext}
  // documentType: Labor_Contract, Annex, Notice, Termination, CV, ID_Card, Diploma, Certificate, Medical_Certificate, Other
  // contractNumber: human-readable contract number from HR company (e.g., "K-2024-001")
  uploadFile: (file, personId, contractNumber = '', documentType = 'Labor_Contract') => {
    const formData = new FormData();
    formData.append('contractFile', file);
    return api.post(`/Contracts/upload?personId=${personId}&contractNumber=${encodeURIComponent(contractNumber)}&documentType=${documentType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Get SAS URL to view/download contract file by contract ID
  getFileUrl: (contractId) => api.get(`/Contracts/${contractId}/file`),
  // Get SAS URL for preview by blob path (before saving contract)
  getFilePreview: (blobPath) => api.get(`/Contracts/file-preview?blobPath=${encodeURIComponent(blobPath)}`),
};

export const offerService = {
  getAll: () => api.get('/OfferOfEmployment'),
  getById: (id) => api.get(`/OfferOfEmployment/${id}`),
  create: (offer) => api.post('/OfferOfEmployment', offer),
  update: (id, offer) => api.put(`/OfferOfEmployment/${id}`, offer),
  delete: (id) => api.delete(`/OfferOfEmployment/${id}`),
};

// --- Simple in-memory cache for people profile summaries (TTL 60s) ---
const __profileSummaryCache = new Map();
async function __getProfileSummaryCached(id, ttlMs = 60_000){
  const now = Date.now();
  const hit = __profileSummaryCache.get(id);
  if (hit && (now - hit.t) < ttlMs) return hit.v;
  const v = await api.get(`/people/${id}/profile-summary`);
  __profileSummaryCache.set(id, { v, t: now });
  return v;
}

export const peopleService = {
  // Search by name; optional filter by departmentId
  searchByName: (name = '', departmentId) => api.get(`/people/search?query=${encodeURIComponent(name)}${departmentId?`&departmentId=${departmentId}`:''}`),
  // Paged listing with optional initial letter
  listPaged: ({ page = 1, pageSize = 30, initial, departmentId, active='all' } = {}) => api.get(`/people/list?page=${page}&pageSize=${pageSize}${initial?`&initial=${encodeURIComponent(initial)}`:''}${departmentId?`&departmentId=${departmentId}`:''}${active?`&active=${encodeURIComponent(active)}`:''}`),
  getById: (id) => api.get(`/people/${id}`),
  create: (person) => api.post('/people', person),
  update: (id, person) => api.put(`/people/${id}`, person),
  remove: (id) => api.delete(`/people/${id}`),
  forceRemove: (id) => api.delete(`/people/${id}/force`),
  getProfileSummary: (id) => __getProfileSummaryCached(id),
  invalidateProfileSummary: (id) => { if(id==null) return; __profileSummaryCache.delete(id); },
  getDocumentChecklist: (id) => api.get(`/people/${id}/document-checklist`),
};


export const candidateService = {
  // consolidated to lowercase route
  getAll: () => api.get('/candidates'),
  getById: (id) => api.get(`/candidates/${id}`),
  create: (candidate) => api.post('/candidates', candidate),
  update: (id, candidate) => api.put(`/candidates/${id}`, candidate),
  delete: (id) => api.delete(`/candidates/${id}`),
};

export const entraService = {
  searchUsers: (query = '', top = 10) => api.get(`/admin/entra/users?query=${encodeURIComponent(query)}&top=${top}`),
  listAll: (top = 200, licensedOnly = true) => api.get(`/admin/entra/all-safe?top=${top}&licensedOnly=${licensedOnly}`),
  listByPrefix: (ch = 'A', top = 200) => api.get(`/admin/entra/by-prefix-safe?ch=${encodeURIComponent(ch)}&top=${top}`),
  link: (payload) => api.post('/admin/entra/link', payload),
  unlink: (personId) => api.post('/admin/entra/unlink', { personId }),
};

export const roleService = {
  getAll: () => api.get('/Role'),
  getUserRoles: (userId) => api.get(`/users/${userId}/roles`),
  assignToUser: (roleId, userId) => api.post(`/Role/${roleId}/users/${userId}`),
  revokeFromUser: (roleId, userId) => api.delete(`/Role/${roleId}/users/${userId}`),
};

export const authService = {
  getProfile: () => api.get('/Auth/profile'),
};

export const leaveBalances = {
  getMine: (year) => api.get('/leave-balances/mine', year ? { params: { year } } : undefined),
};


// Recruitment: Skills
export const skillsService = {
  getAll: () => api.get('/skills'),
  getById: (id) => api.get(`/skills/${id}`),
  create: (skill) => api.post('/skills', skill),
  update: (id, skill) => api.put(`/skills/${id}`, skill),
  delete: (id) => api.delete(`/skills/${id}`)
};

// Competency Groups (with graceful fallback to PascalCase routes if backend uses them)
export const competencyGroupsService = {
  async getByPosition(positionId) {
    try {
      return await api.get(`/positions/${positionId}/competency-groups`);
    } catch (e) {
      if (e.response?.status === 404) {
        return await api.get(`/Positions/${positionId}/Competency-Groups`);
      }
      throw e;
    }
  },
  async upsert(groupCode, payload, positionId) {
    try {
      const params = positionId ? { params: { positionId } } : undefined;
      return await api.put(`/competency-groups/${groupCode}`, payload, params);
    } catch (e) {
      if (e.response?.status === 404) {
        const params = positionId ? { params: { positionId } } : undefined;
        return await api.put(`/Competency-Groups/${groupCode}`, payload, params);
      }
      throw e;
    }
  },
  async create(positionId, payload) {
    try {
      return await api.post(`/positions/${positionId}/competency-groups`, payload);
    } catch (e) {
      if (e.response?.status === 404) {
        return await api.post(`/Positions/${positionId}/Competency-Groups`, payload);
      }
      throw e;
    }
  }
};

export const contractProcessingService = {
  extractContractData: (file) => {
    const formData = new FormData();
    formData.append('contractFile', file);
    return api.post('/contract-processing/extract-employee-data', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  extractOfferData: (file) => {
    const formData = new FormData();
    formData.append('offerFile', file);
    return api.post('/contract-processing/extract-offer-data', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  extractCVData: (file) => {
    const formData = new FormData();
    formData.append('cvFile', file);
    return api.post('/contract-processing/extract-cv-data', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const contractTypeService = {
  getAll: () => api.get('/ContractTypes'),
  getById: (id) => api.get(`/ContractTypes/${id}`),
  create: (contractType) => api.post('/ContractTypes', contractType),
  update: (id, contractType) => api.put(`/ContractTypes/${id}`, contractType),
  delete: (id) => api.delete(`/ContractTypes/${id}`)
};

export const leaveService = {
  // Validation endpoints
  validateRequest: (request) => api.post('/leave-requests/validate', request),
  checkOverlap: (startDate, businessDays, excludeRequestId = null) => {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      businessDays: businessDays.toString()
    });
    if (excludeRequestId) {
      params.append('excludeRequestId', excludeRequestId.toString());
    }
    return api.get(`/leave-requests/check-overlap?${params}`);
  },

  // Existing leave request endpoints (to be added as needed)
  create: (request) => api.post('/leave-requests', request),
  getAll: () => api.get('/leave-requests'),
  getById: (id) => api.get(`/leave-requests/${id}`),
  update: (id, request) => api.put(`/leave-requests/${id}`, request),
  delete: (id) => api.delete(`/leave-requests/${id}`)
};

// Document Management Service
export const documentService = {
  // Get all document requirements (active only)
  getRequirements: (phase = null) => {
    const params = phase ? `?phase=${encodeURIComponent(phase)}` : '';
    return api.get(`/documents/requirements${params}`);
  },

  // Get requirements grouped by phase
  getRequirementsGrouped: () => api.get('/documents/requirements/grouped'),

  // Document Types CRUD (for admin)
  getDocumentTypes: (includeInactive = false) =>
    api.get(`/documents/types?includeInactive=${includeInactive}`),
  createDocumentType: (data) => api.post('/documents/types', data),
  updateDocumentType: (id, data) => api.put(`/documents/types/${id}`, data),
  deleteDocumentType: (id) => api.delete(`/documents/types/${id}`),

  // Get contract info for a person
  getPersonContract: (personId) => api.get(`/documents/person/${personId}/contract`),

  // Get SAS URL for viewing contract file
  getContractUrl: (personId) => api.get(`/documents/person/${personId}/contract/url`),

  // Get all documents for a person (flat list with optional search/filter)
  getPersonDocuments: (personId, search = null, typeId = null) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (typeId) params.append('typeId', typeId.toString());
    const queryString = params.toString();
    return api.get(`/documents/person/${personId}${queryString ? `?${queryString}` : ''}`);
  },

  // Upload a document for a person
  uploadDocument: (personId, file, typeId, customTypeName = null, expiryDate = null, notes = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (typeId) params.append('typeId', typeId.toString());
    if (customTypeName) params.append('customTypeName', customTypeName);
    if (expiryDate) params.append('expiryDate', expiryDate);
    if (notes) params.append('notes', notes);
    return api.post(`/documents/person/${personId}/upload?${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Bulk upload multiple documents for a person
  // files: array of File objects
  // fileMetadata: array of {fileName, typeId, customTypeName, expiryDate, notes}
  uploadDocumentsBulk: (personId, files, fileMetadata = []) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('fileMetadata', JSON.stringify(fileMetadata));
    return api.post(`/documents/person/${personId}/upload-bulk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Get SAS URL for viewing/downloading a document
  getDocumentUrl: (documentId) => api.get(`/documents/${documentId}/url`),

  // Delete a document
  deleteDocument: (documentId) => api.delete(`/documents/${documentId}`),

  // Update document metadata
  updateDocument: (documentId, data) => api.patch(`/documents/${documentId}`, data),

  // Browse all files in blob storage for a person (including unregistered files)
  browsePersonFiles: (personId) => api.get(`/documents/person/${personId}/browse`),

  // Get SAS URL for any blob path
  getBlobUrl: (blobPath) => api.get(`/documents/blob-url?path=${encodeURIComponent(blobPath)}`),

  // Download a file from blob storage by path (returns blob) - uses backend proxy to avoid CORS
  downloadBlobFile: async (blobPath) => {
    const response = await api.get(`/documents/blob-download?path=${encodeURIComponent(blobPath)}`, {
      responseType: 'blob'
    });
    return response;
  }
};

// Short Absences Service
export const shortAbsenceService = {
  // Employee endpoints
  getMy: (year, month) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month) params.append('month', month);
    return api.get(`/short-absences/my${params.toString() ? '?' + params : ''}`);
  },
  getMyStats: (year, month) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month) params.append('month', month);
    return api.get(`/short-absences/my/stats${params.toString() ? '?' + params : ''}`);
  },
  create: (request) => api.post('/short-absences', request),
  cancel: (id) => api.post(`/short-absences/${id}/cancel`),

  // Approver endpoints
  getPending: () => api.get('/short-absences/pending'),
  approve: (id, comments) => api.post(`/short-absences/${id}/approve`, { comments }),
  reject: (id, reason) => api.post(`/short-absences/${id}/reject`, { reason }),

  // Admin endpoints
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.employeeId) params.append('employeeId', filters.employeeId);
    if (filters.departmentId) params.append('departmentId', filters.departmentId);
    if (filters.status !== undefined) params.append('status', filters.status);
    if (filters.year) params.append('year', filters.year);
    if (filters.month) params.append('month', filters.month);
    return api.get(`/short-absences${params.toString() ? '?' + params : ''}`);
  },
  getById: (id) => api.get(`/short-absences/${id}`),
  getEmployeeStats: (employeeId, year, month) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month) params.append('month', month);
    return api.get(`/short-absences/stats/${employeeId}${params.toString() ? '?' + params : ''}`);
  },
  getSettings: () => api.get('/short-absences/settings'),
  updateSettings: (settings) => api.put('/short-absences/settings', settings),
  exportCsv: (year, month, departmentId) => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (month) params.append('month', month);
    if (departmentId) params.append('departmentId', departmentId);
    return api.get(`/short-absences/export${params.toString() ? '?' + params : ''}`, { responseType: 'blob' });
  }
};

export const companyDocumentService = {
  getAll: (category) => {
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    return api.get(`/company-documents${params}`);
  },
  getCategories: () => api.get('/company-documents/categories'),
  download: (id) => api.get(`/company-documents/${id}/download`),
  upload: (file, title, category, description, effectiveDate, expiryDate) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ title, category });
    if (description) params.append('description', description);
    if (effectiveDate) params.append('effectiveDate', effectiveDate);
    if (expiryDate) params.append('expiryDate', expiryDate);
    return api.post(`/company-documents?${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  update: (id, data) => api.put(`/company-documents/${id}`, data),
  delete: (id) => api.delete(`/company-documents/${id}`)
};

export const projectService = {
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  updateStatus: (id, status) => api.put(`/projects/${id}/status`, { status }),
  delete: (id) => api.delete(`/projects/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/projects/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  export: (id) => api.get(`/projects/${id}/export`, { responseType: 'blob' }),
  getMapData: (id, start, end, mode = 'task') =>
    api.get(`/projects/${id}/map`, { params: { start, end, mode } }),
  updateTaskStatus: (projectId, taskId, status) =>
    api.patch(`/projects/${projectId}/tasks/${taskId}/status`, { status }),
  // RA-S5 (2026-07-17) — updateAssignments / updateCellHours /
  // reassignEmployee / getBulkAssignment / saveBulkAssignment retired
  // along with the ResourceAssignment UI. RA + DailyAllocation
  // schema stays until RA-S6.
};

export const projectGridService = {
  getDetailGrid: (id) => api.get(`/projects/${id}/detail-grid`),
  createTask: (id, data) => api.post(`/projects/${id}/tasks`, data),
  updateTask: (id, taskId, data) => api.put(`/projects/${id}/tasks/${taskId}`, data),
  // E-S4: batched task update — silent so it bypasses the global progress bar
  batchUpdateTasks: (id, patches) =>
    api.patch(`/projects/${id}/tasks/batch`, { patches }, { silent: true }),
  reorderTasks: (id, data) => api.put(`/projects/${id}/tasks/reorder`, data),
  deleteTask: (id, taskId) => api.delete(`/projects/${id}/tasks/${taskId}`),
  // LF-S3: local-first save — persists the whole pending diff in one call
  saveProject: (id, payload) => api.post(`/projects/${id}/save`, payload),
  createRole: (id, data) => api.post(`/projects/${id}/roles`, data),
  updateRole: (id, roleId, data) => api.put(`/projects/${id}/roles/${roleId}`, data),
  deleteRole: (id, roleId) => api.delete(`/projects/${id}/roles/${roleId}`),
  exportProject: (id) => api.get(`/projects/${id}/export`, { responseType: 'blob' }),
  exportProjectExcel: (id) => api.get(`/projects/${id}/export-excel`, { responseType: 'blob' }),
};

export default api;
